import type { GeoPoint, ServiceAreaBounds } from '@/types/api'

/**
 * A sub-area boundary while it is being drawn or reshaped, as plain data.
 *
 * Kept apart from Leaflet for the same reason `features/maps/geo.ts` is: every
 * rule the server will apply on save — enough corners, inside Cairo, no edges
 * crossing — can be checked here first, as the supervisor draws, by functions a
 * test can call without a map. The server still decides; this only means the
 * Save button is never the first place a problem is mentioned.
 */

export interface Draft {
  points: GeoPoint[]
  /** True once the shape has been closed — always, for an existing sub-area being reshaped. */
  closed: boolean
}

export const MIN_CORNERS = 3
export const MAX_CORNERS = 500

/** The same rounding the server stores: six decimals, about 11 cm in Cairo. */
export function roundPoint(point: GeoPoint): GeoPoint {
  return { lat: round6(point.lat), lng: round6(point.lng) }
}

function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

function samePoint(a: GeoPoint, b: GeoPoint): boolean {
  return round6(a.lat) === round6(b.lat) && round6(a.lng) === round6(b.lng)
}

/** Adds a corner at the end — a click on the map while drawing. A click on the last corner again does nothing. */
export function addPoint(draft: Draft, point: GeoPoint): Draft {
  if (draft.closed || draft.points.length >= MAX_CORNERS) return draft
  const last = draft.points[draft.points.length - 1]
  if (last && samePoint(last, point)) return draft
  return { ...draft, points: [...draft.points, roundPoint(point)] }
}

export function movePoint(draft: Draft, index: number, point: GeoPoint): Draft {
  if (index < 0 || index >= draft.points.length) return draft
  const points = draft.points.slice()
  points[index] = roundPoint(point)
  return { ...draft, points }
}

/** Inserts a corner after `index` — what dragging an edge's midpoint does. */
export function insertPoint(draft: Draft, afterIndex: number, point: GeoPoint): Draft {
  if (afterIndex < 0 || afterIndex >= draft.points.length || draft.points.length >= MAX_CORNERS) return draft
  const points = draft.points.slice()
  points.splice(afterIndex + 1, 0, roundPoint(point))
  return { ...draft, points }
}

/**
 * Takes a corner away. A closed shape keeps its three corners — removing one
 * would leave a line, which is not an area and could not be saved.
 */
export function removePoint(draft: Draft, index: number): Draft {
  if (index < 0 || index >= draft.points.length) return draft
  if (draft.closed && draft.points.length <= MIN_CORNERS) return draft
  return { ...draft, points: draft.points.filter((_, i) => i !== index) }
}

/** Removes the last corner placed, while drawing. */
export function undoPoint(draft: Draft): Draft {
  if (draft.closed || draft.points.length === 0) return draft
  return { ...draft, points: draft.points.slice(0, -1) }
}

/** Closes the ring — a click on the first corner — once there are enough corners for an area. */
export function closeDraft(draft: Draft): Draft {
  if (draft.closed || draft.points.length < MIN_CORNERS) return draft
  return { ...draft, closed: true }
}

export function canClose(draft: Draft): boolean {
  return !draft.closed && draft.points.length >= MIN_CORNERS
}

/**
 * The middle of every edge, with the corner it follows — where the handles that
 * add a corner sit. An open shape has no closing edge yet.
 */
export function midpoints(draft: Draft): { afterIndex: number; point: GeoPoint }[] {
  const { points, closed } = draft
  const edges = closed ? points.length : points.length - 1
  const result: { afterIndex: number; point: GeoPoint }[] = []
  for (let i = 0; i < edges; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    result.push({ afterIndex: i, point: { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 } })
  }
  return result
}

/* ------------------------------------------------------------- the checks */

export interface DraftCheck {
  /** Everything the server would refuse, in words the supervisor can act on. */
  problems: string[]
  /** Square kilometres, once there is a shape to measure. */
  areaKm2: number | null
}

/**
 * The server's rules, run as the shape changes. Mirrors `GeoPolygon#of` and the
 * Cairo check in `AreaPricingAdminServiceImpl`.
 */
export function checkDraft(draft: Draft, bounds: ServiceAreaBounds | null): DraftCheck {
  const problems: string[] = []
  const { points } = draft

  if (points.length < MIN_CORNERS) {
    problems.push(`Add at least ${MIN_CORNERS} corners.`)
  } else if (!draft.closed) {
    problems.push('Close the shape by clicking its first corner.')
  }
  if (bounds && points.some((point) => !insideBounds(point, bounds))) {
    problems.push('Part of the shape is outside Cairo. Keep every corner inside the dashed rectangle.')
  }

  const areaKm2 = points.length >= MIN_CORNERS ? polygonAreaKm2(points) : null
  if (points.length >= MIN_CORNERS) {
    if (areaKm2 !== null && areaKm2 < 1e-6) {
      problems.push('The shape has no area. Spread the corners out.')
    } else if (selfIntersects(points, draft.closed)) {
      problems.push('Two edges cross. Move the corners so the outline does not cross itself.')
    }
  }

  return { problems, areaKm2 }
}

export function insideBounds(point: GeoPoint, bounds: ServiceAreaBounds): boolean {
  return (
    point.lat >= bounds.minLat &&
    point.lat <= bounds.maxLat &&
    point.lng >= bounds.minLng &&
    point.lng <= bounds.maxLng
  )
}

const KM_PER_DEGREE_LAT = 110.574
const KM_PER_DEGREE_LNG_AT_EQUATOR = 111.32

/** Square kilometres on a flat projection centred on the shape — the server's figure, to the decimal. */
export function polygonAreaKm2(points: GeoPoint[]): number {
  if (points.length < MIN_CORNERS) return 0
  const lats = points.map((point) => point.lat)
  const meanLat = (Math.min(...lats) + Math.max(...lats)) / 2
  const kmPerDegreeLng = KM_PER_DEGREE_LNG_AT_EQUATOR * Math.cos((meanLat * Math.PI) / 180)

  let twiceArea = 0
  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    twiceArea += a.lng * kmPerDegreeLng * (b.lat * KM_PER_DEGREE_LAT) - b.lng * kmPerDegreeLng * (a.lat * KM_PER_DEGREE_LAT)
  }
  return Math.abs(twiceArea) / 2
}

/**
 * Whether two edges cross or touch anywhere but the corner they share, or an
 * edge folds back along the one before it. For an open shape the closing edge
 * does not exist yet, so it is not tested.
 */
export function selfIntersects(points: GeoPoint[], closed = true): boolean {
  const count = points.length
  if (count < 3) return false
  const edges = closed ? count : count - 1

  for (let i = 0; i < edges; i++) {
    const a1 = points[i]
    const a2 = points[(i + 1) % count]

    const hasNext = closed || i + 2 < count
    if (hasNext) {
      const next = points[(i + 2) % count]
      if (orientation(a1, a2, next) === 0 && dot(a1, a2, next) > 0) return true
    }

    for (let j = i + 2; j < edges; j++) {
      if (closed && i === 0 && j === count - 1) continue
      const b1 = points[j]
      const b2 = points[(j + 1) % count]
      if (segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }
  return false
}

function segmentsIntersect(p1: GeoPoint, p2: GeoPoint, p3: GeoPoint, p4: GeoPoint): boolean {
  const d1 = orientation(p3, p4, p1)
  const d2 = orientation(p3, p4, p2)
  const d3 = orientation(p1, p2, p3)
  const d4 = orientation(p1, p2, p4)

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true
  }
  return (
    (d1 === 0 && onSegment(p3, p4, p1)) ||
    (d2 === 0 && onSegment(p3, p4, p2)) ||
    (d3 === 0 && onSegment(p1, p2, p3)) ||
    (d4 === 0 && onSegment(p1, p2, p4))
  )
}

/** Positive for a left turn a → b → c, negative for a right one, zero when in line. x is longitude. */
function orientation(a: GeoPoint, b: GeoPoint, c: GeoPoint): number {
  return (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng)
}

function dot(a: GeoPoint, b: GeoPoint, c: GeoPoint): number {
  return (a.lng - b.lng) * (c.lng - b.lng) + (a.lat - b.lat) * (c.lat - b.lat)
}

function onSegment(a: GeoPoint, b: GeoPoint, p: GeoPoint): boolean {
  return (
    p.lng >= Math.min(a.lng, b.lng) &&
    p.lng <= Math.max(a.lng, b.lng) &&
    p.lat >= Math.min(a.lat, b.lat) &&
    p.lat <= Math.max(a.lat, b.lat)
  )
}

/** The rectangle a set of points spans, for fitting the map to a sub-area. */
export function boundsOf(points: GeoPoint[]): ServiceAreaBounds | null {
  if (points.length === 0) return null
  const lats = points.map((point) => point.lat)
  const lngs = points.map((point) => point.lng)
  return {
    minLat: Math.min(...lats),
    minLng: Math.min(...lngs),
    maxLat: Math.max(...lats),
    maxLng: Math.max(...lngs),
  }
}
